import React from 'react';
import { useFetch, fmtDate, fmtDateTime } from '../utils';
import { adminApi } from '../api';
import { Section } from '../_primitives/Section';
import { DataTable, type Column } from '../_primitives/DataTable';
import { ComparisonSlider } from '../../ComparisonSlider';
import { API_BASE } from '../../../services/api';
import type { RenderRow, UsageRow } from '../types';

const renderCols: Column<RenderRow>[] = [
  { key: 'comparison', label: 'Voor / na',
    render: (r) => (
      <div className="relative isolate w-48 aspect-[4/3] overflow-hidden rounded-lg bg-stone-100">
        {r.staged_filename ? (
          r.original_filename ? (
            <ComparisonSlider
              beforeImage={`${API_BASE}/api/renders/${r.original_filename}`}
              afterImage={`${API_BASE}/api/renders/${r.staged_filename}`}
              labelBefore="Voor"
              labelAfter="Na"
            />
          ) : (
            <img src={`${API_BASE}/api/renders/${r.staged_filename}`} alt="Restyling" className="w-full h-full object-cover" />
          )
        ) : (
          <span className="flex h-full items-center justify-center text-xs text-ink/55">Geen afbeelding</span>
        )}
      </div>
    ) },
  { key: 'created_at', label: 'Datum', sortValue: (r) => r.created_at,
    render: (r) => <span className="text-ink/55 dark:text-white/55">{fmtDateTime(r.created_at)}</span> },
  { key: 'email', label: 'Gebruiker', sortValue: (r) => r.email ?? '', searchValue: (r) => r.email ?? '',
    render: (r) => <span className="text-ink/85 dark:text-white/85">{r.email ?? '—'}</span> },
  { key: 'address', label: 'Woning', searchValue: (r) => r.address ?? '',
    render: (r) => <span className="text-ink/65 dark:text-white/65 truncate max-w-[28ch] inline-block" title={r.address ?? ''}>{r.address ?? '—'}</span> },
  { key: 'mode', label: 'Mode', sortValue: (r) => r.mode ?? '', searchValue: (r) => r.mode ?? '',
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.mode ?? '—'}</span> },
  { key: 'room_type', label: 'Ruimte', sortValue: (r) => r.room_type ?? '',
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.room_type ?? '—'}</span> },
  { key: 'generation_type', label: 'Type', sortValue: (r) => r.generation_type ?? '',
    render: (r) => <span className="text-ink/65 dark:text-white/65">{r.generation_type ?? '—'}</span> },
];

const usageCols: Column<UsageRow>[] = [
  { key: 'email', label: 'Email', sortValue: (u) => u.email, searchValue: (u) => u.email,
    render: (u) => <span className="font-medium text-ink dark:text-white">{u.email}</span> },
  { key: 'first_name', label: 'Naam', sortValue: (u) => u.first_name ?? '',
    render: (u) => <span className="text-ink/65 dark:text-white/65">{u.first_name ?? '—'}</span> },
  { key: 'total_renders', label: 'Totaal', align: 'right', sortValue: (u) => u.total_renders,
    render: (u) => <span className="font-mono text-ink/85 dark:text-white/85">{u.total_renders}</span> },
  { key: 'renders_30d', label: '30 dagen', align: 'right', sortValue: (u) => u.renders_30d,
    render: (u) => <span className="font-mono text-ink/65 dark:text-white/65">{u.renders_30d}</span> },
  { key: 'last_render', label: 'Laatste', sortValue: (u) => u.last_render ?? '',
    render: (u) => <span className="text-ink/55 dark:text-white/55">{fmtDate(u.last_render)}</span> },
];

export const RendersTab: React.FC = () => {
  const renders = useFetch(adminApi.renders);
  const usage = useFetch(adminApi.usage);

  return (
    <div className="space-y-4">
      <Section title={`Renders (${(renders.data ?? []).length})`} subtitle="Laatste 500">
        <DataTable
          rows={renders.data ?? []}
          loading={renders.loading}
          error={renders.error}
          onRetry={renders.refresh}
          columns={renderCols}
          rowKey={(r) => r.id}
          searchPlaceholder="Zoek op gebruiker, mode, adres…"
        />
      </Section>

      <Section title={`Gebruik per gebruiker (${(usage.data ?? []).length})`}>
        <DataTable
          rows={usage.data ?? []}
          loading={usage.loading}
          error={usage.error}
          onRetry={usage.refresh}
          columns={usageCols}
          rowKey={(u) => u.user_id}
          searchPlaceholder="Zoek op email of naam…"
        />
      </Section>
    </div>
  );
};
